import React, { useContext } from "react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Slider from "@material-ui/core/Slider";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import { AppContext, DispatchContext } from "../../AppContext";
import { useStyles } from "./styles";
import Service from "../../services/service";
import * as actions from "../../actions/actions";
import DividerLabel from "../../components/DividerLabel";
import { ReactComponent as HeatMapIcon } from "../../assets/icons/heat-map.svg";
import { ReactComponent as SectorIcon } from "../../assets/icons/sector.svg";
import { ReactComponent as TableIcon } from "../../assets/icons/table.svg";

const DISPLAY_MODES = ["heatmap", "circular", "table"];
const MODE_ICONS = {
    heatmap: <HeatMapIcon style={{ width: 28, height: 28 }} />,
    circular: <SectorIcon style={{ width: 28, height: 28 }} />,
    table: <TableIcon style={{ width: 28, height: 28 }} />,
};

export default function OptionsPanel({ hoveredCell, threshold, onThresholdChange, displayMode, onDisplayModeChange, dynamicRibbonWidth, onDynamicRibbonWidthChange, showMatchesOnly, onShowMatchesOnlyChange, separateMirroredView, onSeparateMirroredViewChange, collusionLibtexts, showUpload, onCompareLibtexts, selected, onCompare, comparisonName, editingProject }) {
    const classes = useStyles();
    const state = useContext(AppContext);
    const dispatch = useContext(DispatchContext);
    const s = state.strings;
    const tc = s.textComparisons || {};

    const okFiles = collusionLibtexts ? [] : state.uploadedFilesStatus.filter(f => f.Status === "Ok");
    const totalWords = collusionLibtexts
        ? collusionLibtexts.reduce((sum, t) => sum + (t.Words || 0), 0)
        : okFiles.reduce((sum, f) => sum + (f.Words || 0), 0);
    const totalPages = Math.ceil(totalWords / 250);
    const totalTexts = collusionLibtexts ? collusionLibtexts.length : okFiles.length;
    const allDone = collusionLibtexts
        ? collusionLibtexts.length >= 2
        : (state.uploadedFilesStatus.length > 0 && state.uploadedFilesStatus.every(f => f.Status === "Ok"));
    const compareEnabled = allDone && totalTexts >= 2;

    const handleCompare = () => {
        const saveName = (reportId) => {
            if (comparisonName && comparisonName.trim()) {
                Service.UpdateReports({ title: comparisonName.trim(), field: 'title' }, [reportId], null);
            }
        };
        if (collusionLibtexts) {
            Service.createComparisonFromLibtexts(collusionLibtexts.map(t => t.Id)).then(res => {
                if (res.success) {
                    saveName(res.data.reportId);
                    dispatch(actions.openToast({ type: "success", message: tc.comparisonStarted || "Comparison started", status: true }));
                    if (onCompareLibtexts) onCompareLibtexts(res.data.reportId);
                } else {
                    const errors = res.errors && res.errors.length > 0 ? res.errors : ["Unknown error"];
                    dispatch(actions.openToast({ type: "error", message: errors.join(", "), status: true }));
                }
            });
        } else {
            const uploadids = okFiles.map(f => f.UploadId);
            Service.createTextComparison(uploadids).then(res => {
                if (res.success) {
                    saveName(res.data.reportId);
                    dispatch(actions.openToast({ type: "success", message: tc.comparisonStarted || "Comparison started", status: true }));
                    if (onCompare) onCompare(res.data.reportId);
                } else {
                    const errors = res.errors && res.errors.length > 0 ? res.errors : ["Unknown error"];
                    dispatch(actions.openToast({ type: "error", message: errors.join(", "), status: true }));
                }
            });
        }
    };

    const showCompareButton = !!collusionLibtexts || !!showUpload;
    const isEditMode = showCompareButton || !!editingProject;

    return (
        <div className={classes.propsPanel}>
            <div className={classes.propsPanelTop}>
                {!showCompareButton ? (
                    <div className={classes.cellInfoBox}>
                        {hoveredCell ? (() => {
                            const mw = hoveredCell.mw ?? null;
                            const fmtStat = (words, pct) => {
                                const matchStr = mw != null && words != null ? `${mw}/${words}` : null;
                                const pctStr = pct != null ? `(${pct.toFixed(1)}%)` : null;
                                return [matchStr, pctStr].filter(Boolean).join(" ") || "–";
                            };
                            return (
                                <>
                                    <div className={classes.propsCellRow}>
                                        <div className={classes.propsCellName}>{hoveredCell.colName}</div>
                                        <div className={classes.propsCellStat}>{fmtStat(hoveredCell.colWords, hoveredCell.val2)}</div>
                                    </div>
                                    <div className={classes.propsCellDivider}>↕</div>
                                    <div className={classes.propsCellRow}>
                                        <div className={classes.propsCellName}>{hoveredCell.rowName}</div>
                                        <div className={classes.propsCellStat}>{fmtStat(hoveredCell.rowWords, hoveredCell.val)}</div>
                                    </div>
                                </>
                            );
                        })() : (
                            <div className={classes.propsNoCell}>
                                {tc.hoverHint || "Hover over a cell to see its details"}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={classes.compareButtonOverlay}>
                        <Button
                            className={classes.compareButton}
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={!compareEnabled}
                            endIcon={<CompareArrowsIcon />}
                            onClick={handleCompare}
                        >
                            {tc.compareButton || "Compare"}
                        </Button>
                        <div className={classes.compareButtonData}>
                            {(s.newCheck || {}).requiredCredits
                                ? s.newCheck.requiredCredits.format(totalWords, totalPages, totalTexts)
                                : `${totalWords} Words / ${totalPages} Pages in ${totalTexts} Texts`}
                        </div>
                    </div>
                )}
            </div>
            <div className={classes.propsPanelScroll} style={isEditMode ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>

                <DividerLabel label={tc.displayOptions || "Display Options"} />
                <div style={{ padding: "4px 0 0" }}>
                    <div style={{ color: "#555", marginBottom: 6 }}>{tc.similarityThreshold || "Similarity Range"}</div>
                    <Slider
                        value={threshold}
                        onChange={(_, v) => onThresholdChange(v)}
                        min={0}
                        max={100}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={v => `${v}%`}
                        color="primary"
                        style={{ color: "#1565c0" }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginTop: -4 }}>
                        <span>0%</span><span>100%</span>
                    </div>
                    {(() => {
                        if (!selected) return <div style={{ marginTop: 6, visibility: "hidden" }}>&nbsp;</div>;
                        const [lo, hi] = Array.isArray(threshold) ? threshold : [0, threshold];
                        const total = selected.sources.length;
                        const shown = selected.sources.filter(s => s.percent >= lo && s.percent <= hi).length;
                        const isFiltered = lo > 0 || hi < 100;
                        return isFiltered ? (
                            <div style={{ color: shown < total ? "#e65100" : "#555", marginTop: 6 }}>
                                {tc.showingFiltered
                                    ? tc.showingFiltered.format(shown, total, total === 1 ? (tc.similarity || "similarity") : (tc.similarities || "similarities"))
                                    : `${shown}/${total} ${total === 1 ? (tc.similarity || "similarity") : (tc.similarities || "similarities")} shown`}
                            </div>
                        ) : (
                            <div style={{ color: "#555", marginTop: 6 }}>
                                {tc.showingAll
                                    ? tc.showingAll.format(total, total === 1 ? (tc.similarity || "similarity") : (tc.similarities || "similarities"))
                                    : `Showing all ${total} ${total === 1 ? (tc.similarity || "similarity") : (tc.similarities || "similarities")}`}
                            </div>
                        );
                    })()}
                </div>
                <FormControlLabel
                    control={
                        <Switch
                            color="primary"
                            checked={showMatchesOnly}
                            onChange={(e) => onShowMatchesOnlyChange(e.target.checked)}
                            disabled={displayMode === "table"}
                        />
                    }
                    label={tc.showMatchesOnly || "Show matches only"}
                    style={{ marginTop: 4, color: displayMode === "table" ? "#bbb" : undefined }}
                />

                <DividerLabel label={tc.displayStyle || "Display Style"} />
                <Tabs
                    value={DISPLAY_MODES.indexOf(displayMode)}
                    onChange={(_, idx) => onDisplayModeChange(DISPLAY_MODES[idx])}
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                    style={{ marginBottom: 12 }}
                >
                    <Tab icon={MODE_ICONS.heatmap} label={tc.viewHeatmap || "Heatmap"} style={{ minWidth: 0, fontSize: "0.7rem" }} />
                    <Tab icon={MODE_ICONS.circular} label={tc.viewCircular || "Circular"} style={{ minWidth: 0, fontSize: "0.7rem" }} />
                    <Tab icon={MODE_ICONS.table} label={tc.viewTable || "Table"} style={{ minWidth: 0, fontSize: "0.7rem" }} />
                </Tabs>
                {displayMode === "circular" && (
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                checked={dynamicRibbonWidth}
                                onChange={(e) => onDynamicRibbonWidthChange(e.target.checked)}
                            />
                        }
                        label={tc.dynamicRibbonWidth || "Dynamic ribbon width"}
                        style={{ marginTop: 4 }}
                    />
                )}
                {displayMode === "heatmap" && (
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                checked={separateMirroredView}
                                onChange={(e) => onSeparateMirroredViewChange(e.target.checked)}
                            />
                        }
                        label={tc.separateMirroredView || "Separate mirrored view"}
                        style={{ marginTop: 4 }}
                    />
                )}
            </div>

            <div className={classes.propsPanelBottom}>
                <div style={{ color: "#555", marginBottom: 4 }}>{tc.similarityLegend || "Similarity"}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>0%</span>
                    <div className={classes.legendBar} style={{ flex: 1 }} />
                    <span>100%</span>
                </div>
            </div>
        </div>
    );
}
